import { omit } from 'lodash-es'

import type { SecureMessage } from '../types'
//
import type {
  GetCryptoTradingPairsQueryResponse,
  GetLumiaNetworkQueryResponse,
  GetPaymentMethodsQueryPayload,
  GetPaymentMethodsQueryResponse,
  GetQuoteQueryPayload,
  GetQuoteQueryResponse,
  GetTransactionListRequest,
  GetTransactionListResponse,
  PreorderMutationPayload,
  PreorderMutationResponse
} from './binance.interfaces'

declare const __EMBARK_ASSET_API_URL__: string

const API_URL =
  // checks for custom service url
  (typeof (window as any).__EMBARK_SERVICES__ !== 'undefined' && (window as any).__EMBARK_SERVICES__?.assetApiUrl) ||
  // checks for build-time injected service url
  (typeof __EMBARK_ASSET_API_URL__ !== 'undefined' && __EMBARK_ASSET_API_URL__) ||
  // should not fallback __EMBARK_ASSET_API_URL__ should be defined, remove later
  'https://api.embarkai.io/on-ramp/api'

type SendResponseFn = (messageId: string, data: any, targetOrigin: string) => void
type BroadcastFn = (type: string, data: any, targetOrigin: string) => void

export class BinanceOnrampAPI {
  private sendResponseFn: SendResponseFn
  private broadcastFn: BroadcastFn
  private sseAbortController: AbortController | null = null

  constructor(sendResponseFn: SendResponseFn, broadcastFn: BroadcastFn) {
    this.sendResponseFn = sendResponseFn
    this.broadcastFn = broadcastFn
  }

  private connectSseTxTracing(walletAddress: string, accessToken: string, origin: string): void {
    this.disconnectSse()

    const controller = new AbortController()
    this.sseAbortController = controller

    const url = `${API_URL}/sse/events?walletAddress=${encodeURIComponent(walletAddress)}`

    const start = async () => {
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal
        })

        if (!response.ok || !response.body) {
          throw new Error(`SSE connection failed: ${response.status}`)
        }

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += value
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || ''

          for (const part of parts) {
            if (part.startsWith('event: ping')) continue

            const match = part.match(/^data: (.+)$/m)
            if (match) {
              try {
                const data = JSON.parse(match[1])
                this.broadcastFn('IFRAME_SSE_EVENT', data, origin)
              } catch {
                console.warn('[BINANCE SSE] Failed to parse event data')
              }
            }
          }
        }
      } catch (error: any) {
        if (error?.name === 'AbortError') return
        console.error('[BINANCE SSE] Connection error, reconnecting in 2s...', error)
        setTimeout(() => {
          if (!controller.signal.aborted) {
            start()
          }
        }, 2000)
      }
    }

    start()
  }

  private disconnectSse(): void {
    if (this.sseAbortController) {
      this.sseAbortController.abort()
      this.sseAbortController = null
    }
  }

  // QUERIES & MUTATIONS

  /** https://developers.binance.com/docs/binance_connect/on-ramp-buy-apis/1.get-trading-pairs */
  private async getTradingPairsQuery(accessToken: string): Promise<GetCryptoTradingPairsQueryResponse> {
    try {
      const response = await fetch(`${API_URL}/binance/pairs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(accessToken && { Authorization: `Bearer ${accessToken}` }) }
      })

      const tradingPairs = await response.json()

      return tradingPairs as GetCryptoTradingPairsQueryResponse
    } catch (error) {
      throw error
    }
  }

  /** https://developers.binance.com/docs/binance_connect/on-ramp-buy-apis/2.get-crypto-networks */
  private async getSupportedNetworksQuery(accessToken: string): Promise<GetLumiaNetworkQueryResponse> {
    try {
      const response = await fetch(`${API_URL}/binance/networks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(accessToken && { Authorization: `Bearer ${accessToken}` }) }
      })

      const networksData = await response.json()

      return networksData as GetLumiaNetworkQueryResponse
    } catch (error) {
      throw error
    }
  }

  private async getPaymentMethodsQuery(
    payload: GetPaymentMethodsQueryPayload,
    accessToken: string
  ): Promise<GetPaymentMethodsQueryResponse> {
    try {
      const response = await fetch(`${API_URL}/binance/modes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(accessToken && { Authorization: `Bearer ${accessToken}` }) },
        body: JSON.stringify(payload)
      })

      const paymentMethods = await response.json()

      return paymentMethods as GetPaymentMethodsQueryResponse
    } catch (error) {
      throw error
    }
  }

  private async getQuoteQuery(payload: GetQuoteQueryPayload, accessToken: string): Promise<GetQuoteQueryResponse> {
    try {
      const response = await fetch(`${API_URL}/binance/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(accessToken && { Authorization: `Bearer ${accessToken}` }) },
        body: JSON.stringify(payload)
      })

      const quote = await response.json()

      return quote
    } catch (error) {
      throw error
    }
  }

  private async createPreorderMutation(
    payload: PreorderMutationPayload,
    accessToken: string
  ): Promise<PreorderMutationResponse> {
    try {
      const response = await fetch(`${API_URL}/binance/pre-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(accessToken && { Authorization: `Bearer ${accessToken}` }) },
        body: JSON.stringify(payload)
      })

      const preorder = await response.json()

      return preorder as PreorderMutationResponse
    } catch (error) {
      throw error
    }
  }

  private async getTransactionList(
    params: GetTransactionListRequest,
    accessToken: string
  ): Promise<GetTransactionListResponse> {
    try {
      // const { walletAddress, txStatuses, page, pageSize } = params
      const txStatus = Array.isArray(params.txStatuses) ? (params.txStatuses || []).join(',') : null

      const apiUrl = new URL(`${API_URL}/binance/transactions`)

      apiUrl.searchParams.append('walletAddress', params.walletAddress as string)

      if (!!txStatus?.length) apiUrl.searchParams.append('txStatus', txStatus)

      if (!!params.page) apiUrl.searchParams.append('page', params.page.toString())
      if (!!params.pageSize) apiUrl.searchParams.append('pageSize', params.pageSize.toString())

      const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...(accessToken && { Authorization: `Bearer ${accessToken}` }) }
      })

      const transactions = await response.json()

      return transactions
    } catch (error) {
      throw error
    }
  }

  // IFRAME MESSAGING

  public async handleMessage(message: SecureMessage, origin: string): Promise<void> {
    const { type, messageId, data } = message

    const accessToken = data.accessToken || ''
    const payload = omit(data, ['accessToken'])

    console.info('[ BINANCE Received message ]', type, payload)

    switch (type) {
      case 'BINANCE_GET_TRADING_PAIRS_NETWORK_QUERY':
        const tradingPairsResponse = await this.getTradingPairsQuery(accessToken)
        this.sendResponseFn(messageId, tradingPairsResponse, origin)

        break

      case 'BINANCE_GET_SUPPORTED_NETWORKS_QUERY':
        const supportedNetworksResponse = await this.getSupportedNetworksQuery(accessToken)
        this.sendResponseFn(messageId, supportedNetworksResponse, origin)

        break

      case 'BINANCE_GET_PAYMENT_METHODS_QUERY':
        const paymentMethodsResponse = await this.getPaymentMethodsQuery(
          payload as GetPaymentMethodsQueryPayload,
          accessToken
        )
        this.sendResponseFn(messageId, paymentMethodsResponse, origin)

        break

      case 'BINANCE_GET_QUOTE_QUERY':
        const quoteResponse = await this.getQuoteQuery(payload as GetQuoteQueryPayload, accessToken)
        this.sendResponseFn(messageId, quoteResponse, origin)

        break

      case 'BINANCE_CREATE_PREORDER_MUTATION':
        const preorderResponse = await this.createPreorderMutation(payload as PreorderMutationPayload, accessToken)
        this.sendResponseFn(messageId, preorderResponse, origin)

        break

      case 'BINANCE_GET_TRANSACTION_LIST_QUERY':
        const txListResponse = await this.getTransactionList(payload as GetTransactionListRequest, accessToken)
        this.sendResponseFn(messageId, txListResponse, origin)

        break

      case 'BINANCE_CONNECT_TX_TRACING':
        this.connectSseTxTracing(payload.walletAddress as string, accessToken, origin)
        this.sendResponseFn(messageId, { connected: true }, origin)

        break

      case 'BINANCE_DISCONNECT_TX_TRACING':
        this.disconnectSse()
        this.sendResponseFn(messageId, { disconnected: true }, origin)

        break

      default:
        throw new Error(`[ BINANCE ]: Unknown message type: ${type}`)
    }
  }
}
